import { Features, Permission } from '@libs/focus-frontend-services/src/AuthenticationService';
import { createSelector } from 'reselect';

import { selectAccountState } from '../Account/selectors';

import { UserState } from './reducer';

export const selectUserState = (state: { user: UserState }) => state.user;

export const selectUser = selectUserState;

export const selectEmployeeId = createSelector([selectUserState], (user) => user.employeeId);

export const selectUuid = createSelector([selectUserState], (user) => user.uuid);

export const selectPopularCountries = createSelector([selectUserState], (user) => user.popularCountries ?? []);

export const selectLegacyFeatureFlags = createSelector([selectUserState], (user) => user.featureFlagsLegacy ?? {});

export const selectLegacyFeatureFlagValue = (flag: string) =>
  createSelector([selectLegacyFeatureFlags], (featureFlags) => (featureFlags[flag]?.value ?? 'off') === 'on');

export const selectLegacyFeatureFlag = (flag: string) =>
  createSelector([selectLegacyFeatureFlags], (featureFlags) => {
    return featureFlags[flag] ?? null;
  });

export const selectEnabledLegacyFeatureFlags = createSelector([selectLegacyFeatureFlags], (featureFlags) =>
  Object.entries(featureFlags).reduce((result, [key, flag]) => {
    if (flag.value === 'on') {
      return [...result, key];
    }
    return result;
  }, [] as string[]),
);

export const selectIsAllowedToChangeSecondAccountManager = createSelector(
  selectLegacyFeatureFlagValue('second_company_account_manager'),
  (isAllowedToChangeSecondAccountManager) => isAllowedToChangeSecondAccountManager,
);

export const selectSecondAccountManagerFlag = createSelector(
  selectLegacyFeatureFlag('second_company_account_manager'),
  (secondAccountManagerFlag) => secondAccountManagerFlag,
);

export const selectDefaultCountry = createSelector([selectUserState], (user) => user.defaultCountry);

export const selectDepartmentCountries = createSelector([selectUserState], (user) => user.departmentCountries);

/**
 * Features are for accounts, permissions for specific users,
 * sometimes they are called the same, depending on how granular
 * the guard is.
 */
export const selectFeatures = createSelector(selectUserState, (user) => user.features ?? []);

export const selectPermissions = createSelector([selectUserState], (user) => user.permissions ?? []);

export const selectHasAccessToDeals = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.Deals),
);

export const selectHasAccessToProjects = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.Projects),
);

export const selectHasAccessToNextgenProjects = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.NextgenProjects),
);

export const selectHasAccessToInvoices = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.Invoices),
);

export const selectHasAccessToEvents = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.Events),
);

export const selectAccountHasAccessToPlanning = createSelector(selectFeatures, (features) =>
  features.includes(Features.Planning),
);

export const selectUserHasAccessToPlanning = createSelector(
  selectAccountHasAccessToPlanning,
  selectPermissions,
  (accountHasAccessToPlanning, permissions) => accountHasAccessToPlanning && permissions.includes(Permission.Calendar),
);

export const selectAccountHasAccessToTimeline = createSelector(selectFeatures, (features) =>
  features.includes(Features.ProjectTimeline),
);

export const selectAccountHasAccessToAdvancedHr = createSelector(selectFeatures, (features) =>
  features.includes(Features.AdvancedHr),
);

export const selectHasAccessToTickets = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.Tickets),
);

export const selectHasAccessToTimeTracking = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.TimeTracking),
);

export const selectHasAccessToUsers = createSelector([selectPermissions], (permissions) =>
  permissions.includes(Permission.Users),
);

export const featureFlagValueSelectorFactory = (flag: string) =>
  createSelector([selectUserState], (user) => (user.featureFlags ?? {})[flag]);

export const accountFeatureFlagValueSelectorFactory = (flag: string) =>
  createSelector([selectAccountState], (account) => (account.featureFlags ?? {})[flag]);

export const selectEnabledFeatureFlags = createSelector([selectUserState], (user) =>
  Object.entries(user.featureFlags ?? {}).reduce((result, [key, value]) => {
    if (value === true) {
      return [...result, key];
    }
    return result;
  }, [] as string[]),
);

export const selectAccessibleModules = createSelector([selectUserState], (user) => user.accessibleModules);

export const selectEnabledAccountFeatureFlags = (state: any) => {
  return Object.entries(state.account.featureFlags ?? {}).reduce(
    (enabledFlags, [key, value]) => (value === true ? [...enabledFlags, key] : enabledFlags),
    [] as string[],
  );
};

export const selectIsAdministrator = createSelector([selectUserState], (user) => user.isAdministrator);

export const selectIsAllowedToExportExcel = createSelector([selectUserState], (user) => user.isAllowedToExportExcel);

export const selectExternalFilePlatformType = createSelector(
  [selectUserState],
  (user) => user.externalFilePlatformType,
);

export const selectPreferences = createSelector([selectUserState], (user) => user.preferences ?? {});

export const selectWebAppPreferences = createSelector([selectUserState], (user) => user.preferences?.webApp ?? {});

export const selectLanguage = createSelector([selectUserState], (user) => user.preferences?.language ?? 'en-GB');
